import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { atividadesAPI, usersAPI, projectsAPI, empresaAPI, cargosAPI } from '../lib/api';
import { filtrarUsuariosAtribuicao } from '../lib/users';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Plus, ClipboardList, CheckCircle2, Clock, AlertTriangle, Flame,
  MessageSquare, RotateCcw, XCircle, Send, Pencil, ExternalLink, FolderOpen,
  Search, ArrowRightLeft,
} from 'lucide-react';
import { cn } from '../lib/utils';

const PRIORIDADE_CFG = {
  baixa: { label: 'Baixa', color: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300', icon: Clock },
  media: { label: 'Média', color: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300', icon: AlertTriangle },
  alta:  { label: 'Alta',  color: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300', icon: Flame },
};

const STATUS_CFG = {
  pendente:     { label: 'Pendente',     color: 'bg-red-500 text-white',     dot: 'bg-red-500' },
  em_andamento: { label: 'Em Andamento', color: 'bg-amber-500 text-white',   dot: 'bg-amber-500' },
  resolvida:    { label: 'Finalizada',   color: 'bg-emerald-500 text-white', dot: 'bg-emerald-500' },
  cancelada:    { label: 'Cancelada',    color: 'bg-slate-400 text-white',   dot: 'bg-slate-400' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const Atividades = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('recebidas');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Permissões granulares do cargo (carregadas uma vez)
  const [podeFinalizarTodas, setPodeFinalizarTodas] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ projeto_id: '', titulo: '', descricao: '', atribuido_a_id: '', prioridade: 'media' });

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [resposta, setResposta] = useState('');
  const [exibirNumero, setExibirNumero] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // Mapeamento de aba → tipo no backend
      // 'concluidas' usa tipo 'todas' do escopo do usuário (master vê tudo)
      const tipoApi = tab === 'concluidas' ? 'todas' : tab;
      const resp = await atividadesAPI.list({ tipo: tipoApi });
      setItems(resp.data || []);
    } catch {
      toast.error('Erro ao carregar atividades');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Filtros derivados: aba (concluídas vs. abertas) + busca textual
  const displayItems = React.useMemo(() => {
    let list = items;

    // 1) Filtro por aba: concluídas mostra resolvidas + canceladas; demais escondem essas
    if (tab === 'concluidas') {
      list = list.filter(a => a.status === 'resolvida' || a.status === 'cancelada');
    } else {
      list = list.filter(a => a.status !== 'resolvida' && a.status !== 'cancelada');
    }

    // 2) Busca textual (Título, Descrição, Projeto/Cliente, Tipo, CPF, Atribuído a, Criado por)
    const term = (searchTerm || '').trim().toLowerCase();
    if (term) {
      const digits = term.replace(/\D/g, '');
      list = list.filter(a => {
        const fields = [
          a.titulo, a.descricao,
          a.projeto_nome, a.projeto_numero, a.projeto_tipo,
          a.atribuido_a_nome, a.criado_por_nome,
        ].filter(Boolean).map(s => String(s).toLowerCase());
        if (fields.some(f => f.includes(term))) return true;
        // Busca por CPF/CNPJ — só dígitos
        if (digits && a.cliente_cpf) {
          const cpf = String(a.cliente_cpf).replace(/\D/g, '');
          if (cpf.includes(digits)) return true;
        }
        return false;
      });
    }

    return list;
  }, [items, tab, searchTerm]);

  // Auto-abrir atividade via ?id=<id> (vindo do sino ou link externo)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const aid = params.get('id');
    if (!aid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await atividadesAPI.get(aid);
        if (cancelled) return;
        setSelected(r.data);
        setResposta('');
        setDetailOpen(true);
        atividadesAPI.marcarLida(aid).catch(() => {});
        // limpa o query param da URL sem recarregar
        window.history.replaceState({}, '', '/atividades');
      } catch {
        toast.error('Atividade não encontrada');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    atividadesAPI.canCreate().then((r) => setCanCreate(!!r.data.can_create)).catch(() => {});
    usersAPI.list().then((r) => setUsers(r.data || [])).catch(() => {});
    projectsAPI.list({}).then((r) => setProjects(r.data || [])).catch(() => {});
    empresaAPI.getConfig().then((r) => setExibirNumero(r.data?.exibir_numero_projeto !== false)).catch(() => {});
    cargosAPI.getMinhasPermissoes()
      .then((r) => setPodeFinalizarTodas(!!r.data?.finalizar_todas_atividades))
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setForm({ projeto_id: '', titulo: '', descricao: '', atribuido_a_id: '', prioridade: 'media' });
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!form.projeto_id || !form.titulo.trim() || !form.atribuido_a_id) {
      toast.error('Preencha projeto, título e atribuído');
      return;
    }
    try {
      await atividadesAPI.create(form);
      toast.success('Atividade criada');
      setCreateOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar atividade');
    }
  };

  const openDetail = async (a) => {
    try {
      const r = await atividadesAPI.get(a.id);
      setSelected(r.data);
      setResposta('');
      setDetailOpen(true);
      // marcar como lida (o usuário está visualizando)
      atividadesAPI.marcarLida(a.id).catch(() => {});
    } catch {
      toast.error('Erro ao abrir atividade');
    }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const r = await atividadesAPI.get(selected.id);
    setSelected(r.data);
    load();
  };

  const handleResolver = async () => {
    try {
      await atividadesAPI.resolver(selected.id);
      toast.success('Atividade finalizada');
      refreshSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar');
    }
  };

  const handleEmAndamento = async () => {
    try {
      await atividadesAPI.emAndamento(selected.id);
      toast.success('Atividade marcada como em andamento');
      refreshSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar atividade');
    }
  };

  const handleReabrir = async () => {
    try {
      await atividadesAPI.reabrir(selected.id);
      toast.success('Atividade reaberta');
      refreshSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reabrir');
    }
  };

  const handleCancelar = async () => {
    if (!window.confirm('Cancelar esta atividade?')) return;
    try {
      await atividadesAPI.cancelar(selected.id);
      toast.success('Cancelada');
      refreshSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  // ===== Transferir (somente master) =====
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferUserId, setTransferUserId] = useState('');
  const [transferring, setTransferring] = useState(false);
  const isMaster = (user?.role || '').toLowerCase() === 'master';

  const openTransfer = () => {
    setTransferUserId('');
    setTransferOpen(true);
  };

  const handleTransferir = async () => {
    if (!transferUserId) { toast.error('Selecione um usuário'); return; }
    setTransferring(true);
    try {
      await atividadesAPI.transferir(selected.id, transferUserId);
      toast.success('Atividade transferida');
      setTransferOpen(false);
      refreshSelected();
      load(); // recarrega lista para refletir nova atribuição
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao transferir');
    } finally {
      setTransferring(false);
    }
  };

  const handleResposta = async () => {
    if (!resposta.trim()) return;
    try {
      await atividadesAPI.addResposta(selected.id, resposta);
      setResposta('');
      refreshSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao responder');
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="atividades-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-primary" />
            Atividades
          </h1>
          <p className="text-muted-foreground">Crie e acompanhe tarefas atribuídas a usuários em projetos.</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} data-testid="new-atividade-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova Atividade
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="recebidas" data-testid="tab-recebidas">Atribuídas a mim</TabsTrigger>
          <TabsTrigger value="criadas" data-testid="tab-criadas">Criadas por mim</TabsTrigger>
          <TabsTrigger value="todas" data-testid="tab-todas">Todas</TabsTrigger>
          <TabsTrigger value="concluidas" data-testid="tab-concluidas">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Concluídas
          </TabsTrigger>
        </TabsList>

        {/* Caixa de busca */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por título, projeto, cliente, CPF, responsável..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="atv-search-input"
          />
        </div>

        <TabsContent value={tab} className="mt-4 space-y-3">
          {loading ? (
            [1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)
          ) : displayItems.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>
                {searchTerm
                  ? `Nenhuma atividade encontrada para "${searchTerm}".`
                  : (tab === 'concluidas'
                      ? 'Nenhuma atividade concluída ainda.'
                      : 'Nenhuma atividade encontrada.')}
              </p>
            </CardContent></Card>
          ) : (
            displayItems.map((a) => {
              const PIcon = PRIORIDADE_CFG[a.prioridade]?.icon || Clock;
              const isNaoLida = (user?.id === a.atribuido_a_id && !a.lida_atribuido) || (user?.id === a.criado_por_id && !a.lida_criador);
              return (
                <Card
                  key={a.id}
                  className={cn('cursor-pointer hover:border-primary/60 transition-all', isNaoLida && 'border-primary shadow-md')}
                  onClick={() => openDetail(a)}
                  data-testid={`atividade-card-${a.id}`}
                >
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className={cn('mt-0.5 w-2 h-2 rounded-full flex-shrink-0', isNaoLida ? 'bg-primary animate-pulse' : 'bg-transparent')} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{a.titulo}</h3>
                        <div className="flex gap-1 flex-shrink-0">
                          <Badge className={cn('text-xs', STATUS_CFG[a.status]?.color)}>
                            {STATUS_CFG[a.status]?.label || a.status}
                          </Badge>
                          <Badge variant="outline" className={cn('text-xs', PRIORIDADE_CFG[a.prioridade]?.color)}>
                            <PIcon className="w-3 h-3 mr-1" />
                            {PRIORIDADE_CFG[a.prioridade]?.label}
                          </Badge>
                        </div>
                      </div>
                      {a.descricao && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{a.descricao}</p>}

                      {/* Projeto destacado + acesso rápido */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/projetos?project=${a.projeto_id}`); }}
                        className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 text-xs font-medium transition-colors"
                        data-testid={`atv-goto-project-${a.id}`}
                        title="Abrir projeto"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        {a.projeto_numero && exibirNumero && <span className="font-mono opacity-80">{a.projeto_numero}</span>}
                        {a.projeto_nome || 'Projeto'}
                        {a.projeto_tipo && <span className="opacity-70">· {a.projeto_tipo}</span>}
                        <ExternalLink className="w-3 h-3 ml-0.5" />
                      </button>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                        <span>Atribuído a: <span className="font-medium text-foreground">{a.atribuido_a_nome}</span></span>
                        <span>•</span>
                        <span>Criada por: {a.criado_por_nome}</span>
                        <span>•</span>
                        <span>{formatDate(a.created_at)}</span>
                        {a.respostas?.length > 0 && (
                          <>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" /> {a.respostas.length}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>

      {/* Nova Atividade */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova Atividade</DialogTitle>
            <DialogDescription>
              Atribua uma tarefa a um usuário vinculada a um projeto.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Projeto *</Label>
              <Select value={form.projeto_id} onValueChange={(v) => setForm({ ...form, projeto_id: v })}>
                <SelectTrigger data-testid="atv-projeto-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.cliente_nome} — {p.tipo_projeto} ({p.etapa_atual_nome})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Título *</Label>
              <Input
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Ex: Verificar documentação no CAR"
                data-testid="atv-titulo-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Descrição</Label>
              <Textarea
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Detalhe a atividade..."
                rows={4}
                data-testid="atv-descricao-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Atribuído a *</Label>
                <Select value={form.atribuido_a_id} onValueChange={(v) => setForm({ ...form, atribuido_a_id: v })}>
                  <SelectTrigger data-testid="atv-user-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {filtrarUsuariosAtribuicao(users).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Prioridade</Label>
                <Select value={form.prioridade} onValueChange={(v) => setForm({ ...form, prioridade: v })}>
                  <SelectTrigger data-testid="atv-prio-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={submitCreate} data-testid="save-atividade-btn">
              <Plus className="w-4 h-4 mr-2" /> Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalhe / Respostas */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {selected.titulo}
                  <Badge className={cn('text-xs ml-2', STATUS_CFG[selected.status]?.color)}>
                    {STATUS_CFG[selected.status]?.label}
                  </Badge>
                  <Badge variant="outline" className={cn('text-xs', PRIORIDADE_CFG[selected.prioridade]?.color)}>
                    {PRIORIDADE_CFG[selected.prioridade]?.label}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  Atribuída a: <span className="font-medium">{selected.atribuido_a_nome}</span>
                </DialogDescription>
              </DialogHeader>

              {/* Card de acesso rápido ao projeto */}
              <button
                type="button"
                onClick={() => { setDetailOpen(false); navigate(`/projetos?project=${selected.projeto_id}`); }}
                className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors text-left"
                data-testid="atv-detail-goto-project"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Projeto relacionado</p>
                  <p className="font-semibold truncate">
                    {selected.projeto_numero && exibirNumero && (
                      <span className="font-mono text-xs mr-2 px-1.5 py-0.5 rounded bg-background">{selected.projeto_numero}</span>
                    )}
                    {selected.projeto_nome || 'Projeto'}
                  </p>
                  {selected.projeto_tipo && (
                    <p className="text-xs text-muted-foreground">{selected.projeto_tipo}</p>
                  )}
                </div>
                <ExternalLink className="w-4 h-4 text-primary flex-shrink-0" />
              </button>

              <div className="space-y-3">
                {selected.descricao && (
                  <div className="rounded-lg border p-3 bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">
                      Criada por {selected.criado_por_nome} em {formatDate(selected.created_at)}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{selected.descricao}</p>
                  </div>
                )}

                <ScrollArea className="max-h-56 border rounded-lg">
                  <div className="p-2 space-y-2">
                    {selected.respostas?.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2 text-center">Sem respostas ainda.</p>
                    ) : (
                      selected.respostas.map((r, idx) => (
                        <div key={idx} className="p-2 rounded bg-card border">
                          <p className="text-xs text-muted-foreground mb-1">
                            <span className="font-medium text-foreground">{r.usuario_nome}</span> · {formatDate(r.created_at)}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{r.texto}</p>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>

                {(selected.status === 'pendente' || selected.status === 'em_andamento') && (
                  <div className="flex gap-2">
                    <Input
                      value={resposta}
                      onChange={(e) => setResposta(e.target.value)}
                      placeholder="Adicionar resposta/informação..."
                      onKeyDown={(e) => e.key === 'Enter' && handleResposta()}
                      data-testid="atv-resposta-input"
                    />
                    <Button onClick={handleResposta} disabled={!resposta.trim()} data-testid="atv-resposta-send">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                {isMaster && selected.status !== 'resolvida' && selected.status !== 'cancelada' && (
                  <Button
                    variant="outline"
                    onClick={openTransfer}
                    data-testid="atv-transferir-btn"
                    className="border-indigo-400 text-indigo-700 hover:bg-indigo-50"
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-2" /> Transferir
                  </Button>
                )}
                {((user?.id === selected.criado_por_id) || podeFinalizarTodas) && selected.status !== 'resolvida' && selected.status !== 'cancelada' && (
                  <Button variant="ghost" onClick={handleCancelar} className="text-destructive" data-testid="atv-cancelar-btn">
                    <XCircle className="w-4 h-4 mr-2" /> Cancelar
                  </Button>
                )}
                {user?.id === selected.criado_por_id && selected.status === 'resolvida' && (
                  <Button variant="outline" onClick={handleReabrir} data-testid="atv-reabrir-btn">
                    <RotateCcw className="w-4 h-4 mr-2" /> Reabrir
                  </Button>
                )}
                {user?.id === selected.atribuido_a_id && selected.status === 'pendente' && (
                  <Button variant="outline" onClick={handleEmAndamento} data-testid="atv-iniciar-btn" className="border-amber-400 text-amber-700 hover:bg-amber-50">
                    <Clock className="w-4 h-4 mr-2" /> Iniciar
                  </Button>
                )}
                {((user?.id === selected.atribuido_a_id) || podeFinalizarTodas) && (selected.status === 'pendente' || selected.status === 'em_andamento') && (
                  <Button onClick={handleResolver} data-testid="atv-resolver-btn" className="bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Finalizar
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDetailOpen(false)}>Fechar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Modal de Transferência (Master only) */}
      {isMaster && (
        <Dialog open={transferOpen} onOpenChange={(v) => { if (!v) setTransferOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="dialog-transferir-atv">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4 text-indigo-600" />
                Transferir Atividade
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Atribuído atualmente a: <strong>{selected?.atribuido_a_nome || '—'}</strong>
              </p>
              <div>
                <Label>Novo responsável *</Label>
                <Select value={transferUserId} onValueChange={setTransferUserId}>
                  <SelectTrigger data-testid="transferir-user-select">
                    <SelectValue placeholder="Selecione um usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    {filtrarUsuariosAtribuicao(users)
                      .filter(u => u.id !== selected?.atribuido_a_id)
                      .map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Um comentário automático será adicionado registrando a transferência.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancelar</Button>
              <Button onClick={handleTransferir} disabled={transferring || !transferUserId} data-testid="btn-confirmar-transferir">
                {transferring ? 'Transferindo...' : 'Transferir'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Atividades;
